import { ReactNode } from 'react';
import {
  CreditCard,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  usePaymentListQuery,
  usePaymentStatusCountQuery,
} from '@/client/api/payment/payment.query';
import {
  derivePaymentStatus,
  formatMoney,
  Payment,
  PaymentListParams,
  PaymentListStatus,
  paymentChannel,
  paymentEntityLink,
} from '@/client/types/payment';
import { usePaymentListUrlState } from '@/client/hooks/usePaymentListUrlState';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import Pagination from '@/client/components/common/Pagination';
import PaymentDetailsDrawer from '@/client/components/admin/Payment/PaymentDetailsDrawer';
import { PaymentsFilterBar, SortableTh } from '@/client/components/admin/Payment/PaymentsFilterBar';
import {
  EntityRelationshipLink,
  OrgRelationshipLink,
  RelationshipLink,
  UserRelationshipLink,
} from '@/client/components/admin/Payment/RelationshipLink';

import { ExportCsvButton } from '@/client/components/admin/Payment/ExportCsvButton';
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  completed: 'Completed',
  canceled: 'Canceled',
  refunded: 'Refunded',
};

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  canceled: 'bg-gray-100 text-gray-600',
  refunded: 'bg-red-100 text-red-800',
};

const STATUS_ICON: Record<string, ReactNode> = {
  pending: <Clock className="h-3.5 w-3.5" />,
  completed: <CheckCircle2 className="h-3.5 w-3.5" />,
  canceled: <XCircle className="h-3.5 w-3.5" />,
  refunded: <RefreshCw className="h-3.5 w-3.5" />,
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'refunded', label: 'Refunded' },
];

const StatCard = ({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: ReactNode;
  color: string;
}) => (
  <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-4">
      <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="mt-0.5 text-2xl font-black text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

const SkeletonRow = () => (
  <tr>
    {Array.from({ length: 8 }).map((_, i) => (
      <td key={i} className="px-6 py-4">
        <Skeleton className="h-4 w-20" />
      </td>
    ))}
  </tr>
);

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
};

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing payments requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">payment.view</code> permission.
    </p>
  </div>
);

const PaymentListPage = () => {
  const { state, offset, patch, clearFilters, setDetail, toggleSort, toApiNumber } =
    usePaymentListUrlState();

  useBreadcrumbs([
    { label: 'Payments', href: '/admin/payments' },
    { label: state.status ? STATUS_LABELS[state.status] || state.status : 'All' },
  ]);

  const viewPerm = useHasPermission('payment.view');
  const editPerm = useHasPermission('payment.edit');
  const refundPerm = useHasPermission('payment.refund');
  const voidPerm = useHasPermission('payment.void');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canEdit = editPerm.isSuperAdmin || editPerm.hasPermission;
  const canRefund = refundPerm.isSuperAdmin || refundPerm.hasPermission;
  const canVoid = voidPerm.isSuperAdmin || voidPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const listParams: PaymentListParams = {
    limit: state.limit,
    offset,
    ...(state.q.trim() ? { q: state.q.trim() } : {}),
    ...(state.status ? { status: state.status as PaymentListStatus } : {}),
    ...(state.uid ? { uid: toApiNumber(state.uid) } : {}),
    ...(state.oid ? { oid: toApiNumber(state.oid) } : {}),
    ...(state.ccode ? { ccode: state.ccode } : {}),
    ...(state.date_from ? { date_from: state.date_from } : {}),
    ...(state.date_to ? { date_to: state.date_to } : {}),
    ...(state.amount_min ? { amount_min: toApiNumber(state.amount_min) } : {}),
    ...(state.amount_max ? { amount_max: toApiNumber(state.amount_max) } : {}),
    ...(state.sort_by ? { sort_by: state.sort_by, order: state.order || 'desc' } : {}),
  };

  const { data: paymentData, isLoading, isError, error } = usePaymentListQuery(
    listParams,
    canView && !permLoading,
  );

  const countEnabled = canView && !permLoading;
  const { data: pendingCount = 0 } = usePaymentStatusCountQuery('pending', countEnabled);
  const { data: completedCount = 0 } = usePaymentStatusCountQuery('completed', countEnabled);
  const { data: canceledCount = 0 } = usePaymentStatusCountQuery('canceled', countEnabled);
  const { data: refundedCount = 0 } = usePaymentStatusCountQuery('refunded', countEnabled);

  const payments = paymentData?.payments ?? [];
  const pagination = paymentData?.pagination;
  const forbidden =
    (error as { status?: number })?.status === 403 || (!permLoading && !canView);

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <CreditCard className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">
              {state.status ? `${STATUS_LABELS[state.status] || state.status} payments` : 'Payments'}
            </h1>
          </div>
          <p className="text-sm text-gray-500">Admin payments — verify, fulfill, refund, cancel.</p>
          <div className="mt-3">
            <ExportCsvButton path="/api/admin/v2/payments" params={listParams} filename="payments.csv" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard
            label="Total"
            value={(pagination?.total ?? 0).toLocaleString()}
            icon={<CreditCard className="h-6 w-6 text-blue-600" />}
            color="bg-blue-100"
          />
          <StatCard
            label="Pending"
            value={pendingCount.toLocaleString()}
            icon={<Clock className="h-6 w-6 text-yellow-600" />}
            color="bg-yellow-100"
          />
          <StatCard
            label="Completed"
            value={completedCount.toLocaleString()}
            icon={<CheckCircle2 className="h-6 w-6 text-green-600" />}
            color="bg-green-100"
          />
          <StatCard
            label="Canceled"
            value={canceledCount.toLocaleString()}
            icon={<XCircle className="h-6 w-6 text-gray-500" />}
            color="bg-gray-100"
          />
          <StatCard
            label="Refunded"
            value={refundedCount.toLocaleString()}
            icon={<RefreshCw className="h-6 w-6 text-red-600" />}
            color="bg-red-100"
          />
        </div>

        <PaymentsFilterBar
          state={state}
          onPatch={patch}
          onClear={() => clearFilters()}
          statusOptions={STATUS_OPTIONS}
          showAmount
          showDates
          showOid
          showCcode
          searchPlaceholder="Search ffid, uuid, or ref…"
        />

        {isError && !forbidden && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {(error as Error)?.message || 'Failed to load payments'}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Payment
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    User
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Org
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Invoice
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Entity
                  </th>
                  <SortableTh
                    label="Amount"
                    field="amount"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                  <th className="px-6 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Status
                  </th>
                  <SortableTh
                    label="Date"
                    field="date_added"
                    sortBy={state.sort_by}
                    order={state.order}
                    onToggle={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 bg-white">
                {isLoading &&
                  Array.from({ length: Math.min(state.limit, 10) }).map((_, i) => (
                    <SkeletonRow key={i} />
                  ))}

                {!isLoading && payments.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                          <CreditCard className="h-8 w-8 text-gray-400" />
                        </div>
                        <p className="font-medium text-gray-500">No payments found</p>
                        <p className="text-sm text-gray-400">Try adjusting your filters</p>
                      </div>
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  payments.map((payment: Payment) => {
                    const status = derivePaymentStatus(payment);
                    const entityLink = paymentEntityLink(payment);
                    const entitySummary =
                      payment.entity_summary ||
                      (entityLink
                        ? {
                            eid: entityLink.eid,
                            name: entityLink.name,
                            slug: entityLink.slug,
                            ffid: entityLink.ffid,
                            uuid: entityLink.uuid,
                          }
                        : null);
                    return (
                      <tr
                        key={payment.pid}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() => setDetail('pid', payment.pid)}
                      >
                        <td className="whitespace-nowrap px-6 py-4">
                          <p className="font-mono text-sm font-semibold text-gray-900">
                            #{payment.pid}
                          </p>
                          <p className="font-mono text-xs text-gray-400">
                            {payment.ffid || payment.uuid?.slice(0, 8) || '—'}
                          </p>
                        </td>
                        <td className="max-w-[180px] px-6 py-4">
                          <UserRelationshipLink uid={payment.uid} summary={payment.user_summary} />
                        </td>
                        <td className="max-w-[160px] px-6 py-4">
                          <OrgRelationshipLink oid={payment.oid} summary={payment.org_summary} />
                        </td>
                        <td className="max-w-[180px] px-6 py-4">
                          {payment.invid != null ? (
                            <RelationshipLink
                              kind="invoice"
                              id={payment.invid}
                              label={payment.invoice_ffid || `#${payment.invid}`}
                              subtitle={payment.invoice_status ? String(payment.invoice_status) : undefined}
                            />
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="max-w-[180px] px-6 py-4">
                          <EntityRelationshipLink eid={entitySummary?.eid ?? null} summary={entitySummary} />
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <p className="text-sm font-bold text-gray-900">
                            {formatMoney(payment.amount, payment.ccode)}
                          </p>
                          <p className="text-xs text-gray-400">{payment.ccode || 'GHS'}</p>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                              STATUS_BADGE[status] || STATUS_BADGE.pending
                            }`}
                          >
                            {STATUS_ICON[status]}
                            {status.toUpperCase()}
                          </span>
                          <p className="mt-1 text-xs text-gray-400">{paymentChannel(payment)}</p>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {formatDate(payment.date_added)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-100 bg-white px-6 py-3">
            <Pagination
              total={pagination?.total ?? 0}
              page={state.page}
              itemsPerPage={state.limit}
              limit={state.limit}
              setPage={(page) => patch({ page }, false)}
              setItemsPerPage={(limit) => patch({ limit, page: 1 }, false)}
              showButtons={true}
              showSelect={true}
              selectOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>

      <PaymentDetailsDrawer
        isOpen={state.pid != null}
        setIsOpen={(open) => {
          if (!open) setDetail('pid', null);
        }}
        pid={state.pid}
        canEdit={canEdit}
        canRefund={canRefund}
        canVoid={canVoid}
        onClose={() => setDetail('pid', null)}
      />
    </div>
  );
};

export default PaymentListPage;
